// =====================================================
// ProyeccionCrediticia/index.js
// Componente principal - Proyección Crediticia del Productor
// src/pages/Agro/Productor/ProyeccionCrediticia/index.js
// =====================================================

import React, { useState, useEffect, useCallback, useRef } from "react"
import { getErrorMessage } from "helpers/error_helpers"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Spinner,
  Alert,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Progress,
  Collapse,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LineChart,
  Line,
  Area,
  AreaChart,
  ComposedChart,
  Legend,
} from "recharts"
import dayjs from "dayjs"
import "dayjs/locale/es"

import { getProyeccionCrediticia } from "api/agro"
import toastHandler from "toastHandler"
import MetodologiaProyeccion from "./MetodologiaProyeccion"
import useAuthorization from "hooks/useAuthorization"

dayjs.locale("es")

// Formateo de moneda
const fmtUsd = val => {
  if (val == null) return "—"
  return (
    "USD " +
    Number(val).toLocaleString("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  )
}

const fmtNum = (val, dec = 2) => {
  if (val == null) return "—"
  return Number(val).toLocaleString("es-AR", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  })
}

const colorConfianza = nivel => {
  switch (nivel) {
    case "ALTO":
      return "success"
    case "MEDIO":
      return "warning"
    case "BAJO":
      return "danger"
    default:
      return "secondary"
  }
}

const colorDictamen = dictamen => {
  switch (dictamen) {
    case "FAVORABLE":
      return "success"
    case "CONDICIONAL":
      return "warning"
    case "DESFAVORABLE":
      return "danger"
    // BDSJEA-170: gris. La ausencia de datos no se muestra como rechazo.
    case "SIN_DATOS":
      return "secondary"
    default:
      return "secondary"
  }
}

const iconDictamen = dictamen => {
  switch (dictamen) {
    case "FAVORABLE":
      return "✓"
    case "CONDICIONAL":
      return "⚠"
    case "DESFAVORABLE":
      return "✗"
    case "SIN_DATOS":
      return "?"
    default:
      return "?"
  }
}

const DICTAMEN_THEME = {
  FAVORABLE: {
    bg: "#f0faf4", // verde muy pálido
    border: "#34a853", // verde sobrio
    text: "#1a7431", // verde oscuro legible
    badge: "#d4edda",
    badgeText: "#155724",
    icon: "mdi-check-decagram",
    label: "Favorable",
  },
  CONDICIONAL: {
    bg: "#fef9ee", // ámbar muy pálido
    border: "#f59e0b", // ámbar sobrio
    text: "#92400e", // marrón oscuro
    badge: "#fff3cd",
    badgeText: "#856404",
    icon: "mdi-alert-decagram",
    label: "Condicional",
  },
  DESFAVORABLE: {
    bg: "#fef2f2", // rosa muy pálido (NO rojo saturado)
    border: "#b91c1c", // rojo apagado
    text: "#7f1d1d", // borgoña oscuro
    badge: "#f5d5d5",
    badgeText: "#721c24",
    icon: "mdi-close-octagon",
    label: "Desfavorable",
  },
  // BDSJEA-170: gris. No es un rechazo, es que no hay con qué proyectar.
  SIN_DATOS: {
    bg: "#f8fafc",
    border: "#94a3b8",
    text: "#334155",
    badge: "#e2e8f0",
    badgeText: "#334155",
    icon: "mdi-help-circle-outline",
    label: "Sin datos suficientes",
  },
}

const PALETA = {
  verde: {
    bg: "#f0faf4",
    border: "#34a853",
    text: "#1a7431",
    light: "#d4edda",
  },
  azul: { bg: "#eff6ff", border: "#3b82f6", text: "#1e40af", light: "#dbeafe" },
  violeta: {
    bg: "#f5f3ff",
    border: "#8b5cf6",
    text: "#5b21b6",
    light: "#ede9fe",
  },
  ambar: {
    bg: "#fefce8",
    border: "#eab308",
    text: "#854d0e",
    light: "#fef9c3",
  },
  marron: {
    bg: "#fdf8f3",
    border: "#a16207",
    text: "#78350f",
    light: "#fde68a",
  },
  rosa: { bg: "#fdf2f8", border: "#db2777", text: "#9d174d", light: "#fce7f3" },
  gris: { bg: "#f8fafc", border: "#64748b", text: "#334155", light: "#e2e8f0" },
  rojo: { bg: "#fef2f2", border: "#b91c1c", text: "#7f1d1d", light: "#fecaca" },
  teal: { bg: "#f0fdfa", border: "#0d9488", text: "#115e59", light: "#ccfbf1" },
}

const COLORES_CULTIVO = {
  SOJA: PALETA.verde,
  MAIZ: PALETA.ambar,
  TRIGO: PALETA.marron,
  GIRASOL: {
    bg: "#fffbeb",
    border: "#d97706",
    text: "#92400e",
    light: "#fef3c7",
  },
  SORGO: PALETA.rosa,
  CEBADA: PALETA.gris,
  default: PALETA.azul,
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

// Cache a nivel de módulo — sobrevive al desmontaje/remontaje del TabPane
const _cache = {}

const ProyeccionCrediticia = ({ idProductor }) => {
  const cacheKey = `proy_${idProductor}`
  const [loading, setLoading] = useState(false)
  const [proyeccion, setProyeccion] = useState(() => _cache[cacheKey] || null)
  const [error, setError] = useState(null)

  const setErrorToast = toastHandler(state => state.setError)
  // BDSJEA-541: si la pestaña se desmonta mientras carga, no se actualiza estado.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Parámetros opcionales
  const [montoSolicitado, setMontoSolicitado] = useState("")
  const [plazoMeses, setPlazoMeses] = useState(12)
  const [showSupuestos, setShowSupuestos] = useState(false)

  const { puedeModificarObjeto } = useAuthorization()

  const cargarProyeccion = useCallback(
    async forzar => {
      if (!idProductor) return
      // Si hay datos en cache y no se fuerza, no recalcular
      if (!forzar && _cache[cacheKey]) {
        setProyeccion(_cache[cacheKey])
        return
      }
      setLoading(true)
      setError(null)
      try {
        const monto = montoSolicitado ? parseFloat(montoSolicitado) : undefined
        // BDSJEA-169: `forzar` es true solo cuando el usuario aprieta
        // Recalcular. La carga inicial y los cambios de plazo no registran.
        const res = await getProyeccionCrediticia(
          idProductor,
          monto,
          plazoMeses,
          Boolean(forzar),
        )
        _cache[cacheKey] = res // Guardar en cache
        if (mountedRef.current) setProyeccion(res)
      } catch (err) {
        if (!mountedRef.current) return
        // BDSJEA-541: un solo mensaje descriptivo para la pantalla y el toast:
        // el texto `error` del backend (que getErrorMessage no lee) o, si no
        // hay, el mensaje por tipo de error; nunca el texto crudo de axios.
        const detalle = err?.response?.data?.error
        const msg =
          typeof detalle === "string" && detalle.trim()
            ? detalle
            : getErrorMessage(err) || "Error al generar proyección"
        setError(msg)
        setErrorToast(msg)
      } finally {
        if (mountedRef.current) setLoading(false)
      }
    },
    [idProductor, montoSolicitado, plazoMeses, cacheKey],
  )

  useEffect(() => {
    // BDSJEA-541: cargarProyeccion ya apaga `loading` en su propio finally; el
    // `.then` con la condicion de mounted invertida solo llamaba a
    // setError(null) cuando el componente ya estaba desmontado.
    cargarProyeccion(false)
  }, [idProductor])

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-2">Calculando proyección crediticia...</p>
      </div>
    )
  }

  if (error && !proyeccion) {
    return (
      <Alert color="danger">
        <strong>Error:</strong> {error}
        {puedeModificarObjeto("/agro/establecimientos") && (
          <Button
            color="link"
            size="sm"
            onClick={() => cargarProyeccion(true)}
            className="ms-2"
          >
            Reintentar
          </Button>
        )}
      </Alert>
    )
  }

  return (
    <div>
      {/* Barra de parámetros */}
      <Card className="mb-3 border-0 shadow-sm">
        <CardBody className="py-2">
          <Row className="align-items-center">
            <Col md={4}>
              <small className="text-muted d-block mb-1">
                Monto solicitado (USD) — opcional
              </small>
              <InputGroup size="sm">
                <InputGroupText>USD</InputGroupText>
                <Input
                  type="number"
                  value={montoSolicitado}
                  onChange={e => setMontoSolicitado(e.target.value)}
                  placeholder="Ej: 50000"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <small className="text-muted d-block mb-1">Plazo (meses)</small>
              <Input
                type="select"
                bsSize="sm"
                value={plazoMeses}
                onChange={e => setPlazoMeses(parseInt(e.target.value))}
              >
                <option value={6}>6 meses</option>
                <option value={9}>9 meses</option>
                <option value={12}>12 meses</option>
                <option value={18}>18 meses</option>
                <option value={24}>24 meses</option>
              </Input>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              {puedeModificarObjeto("/agro/establecimientos") && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => cargarProyeccion(true)}
                  disabled={loading}
                  className="mt-3"
                >
                  {loading ? <Spinner size="sm" className="me-1" /> : null}
                  Recalcular Proyección
                </Button>
              )}
            </Col>
            <Col md={2} className="text-end">
              {proyeccion && (
                <div className="mt-3">
                  <Badge
                    color={colorConfianza(proyeccion.nivelConfianza)}
                    className="px-2 py-1"
                  >
                    Confianza: {proyeccion.nivelConfianza}
                  </Badge>
                </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <MetodologiaProyeccion />

      {proyeccion && (
        <>
          {/* Dictamen */}
          <DictamenPanel dictamen={proyeccion.dictamen} />

          {/* KPIs principales */}
          <KpisResumen
            resumen={proyeccion.resumen}
            dictamen={proyeccion.dictamen}
          />

          {/* Timeline de cosechas */}
          <TimelineCosechas cultivos={proyeccion.cultivos} />

          {/* Detalle por cultivo */}
          <DetalleCultivos cultivos={proyeccion.cultivos} />

          {/* Flujo de caja mensual */}
          <FlujoCajaMensual flujo={proyeccion.flujoMensual} />

          {/* Ganadera (si aplica) */}
          {proyeccion.ganadera && (
            <SeccionGanadera ganadera={proyeccion.ganadera} />
          )}

          {/* Supuestos */}
          <Card className="mb-3 border-0 shadow-sm text-muted">
            <CardHeader
              className="bg-transparent cursor-pointer py-2"
              onClick={() => setShowSupuestos(!showSupuestos)}
              style={{ cursor: "pointer" }}
            >
              <span className="fw-semibold">
                {showSupuestos ? "▾" : "▸"} Supuestos y parámetros utilizados
              </span>
              <Badge color="info" pill className="ms-2">
                {proyeccion.supuestos?.length || 0}
              </Badge>
            </CardHeader>
            <Collapse isOpen={showSupuestos}>
              <CardBody className="pt-0">
                <small className="text-muted">
                  Estos son los supuestos con los que se calculó la proyección.
                  Los precios y rendimientos pueden variar.
                </small>
                <ul className="mt-2 mb-0" style={{ fontSize: "0.85rem" }}>
                  {(proyeccion.supuestos || []).map((s, i) => (
                    <li key={i} className="text-muted">
                      {s}
                    </li>
                  ))}
                </ul>
                {proyeccion.advertencias?.length > 0 && (
                  <Alert color="warning" className="mt-2 mb-0 py-2">
                    <strong>Advertencias:</strong>
                    <ul className="mb-0 mt-1">
                      {proyeccion.advertencias.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </CardBody>
            </Collapse>
          </Card>
        </>
      )}
    </div>
  )
}

// =====================================================
// SUB-COMPONENTES
// =====================================================

// --- Panel de Dictamen ---
const DictamenPanel = ({ dictamen, nivelConfianza }) => {
  if (!dictamen) return null

  // BDSJEA-170: el fallback a CONDICIONAL hacía que un dictamen desconocido se
  // mostrara con etiqueta y color de "Condicional". Con SIN_DATOS eso llegó a
  // pantalla: el backend devolvía SIN_DATOS y el informe decía "Condicional".
  // Un estado que no se conoce no debe disfrazarse de uno que sí.
  const theme = DICTAMEN_THEME[dictamen.dictamen] || {
    ...DICTAMEN_THEME.SIN_DATOS,
    label: dictamen.dictamen || "Sin dictamen",
  }

  return (
    <Card className="mb-3 border-0 shadow-sm overflow-hidden">
      {/* Barra superior de color sutil */}
      <div style={{ height: "4px", background: theme.border }} />

      <CardBody className="py-3" style={{ backgroundColor: theme.bg }}>
        <Row className="align-items-center">
          {/* Ícono + dictamen */}
          <Col md={5}>
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: 52,
                  height: 52,
                  backgroundColor: theme.badge,
                  border: `2px solid ${theme.border}`,
                }}
              >
                <i
                  className={`mdi ${theme.icon}`}
                  style={{ fontSize: "1.5rem", color: theme.border }}
                />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: theme.text,
                      opacity: 0.7,
                    }}
                  >
                    Dictamen crediticio
                  </span>
                  {nivelConfianza && (
                    <span
                      className="px-2 py-0 rounded-pill"
                      style={{
                        fontSize: "0.65rem",
                        backgroundColor: "#e9ecef",
                        color: "#495057",
                      }}
                    >
                      Confianza: {nivelConfianza}
                    </span>
                  )}
                </div>
                <h4
                  className="mb-0"
                  style={{ color: theme.text, fontWeight: 700 }}
                >
                  {theme.label}
                </h4>
                {dictamen.recomendacion && (
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "0.85rem",
                      color: theme.text,
                      opacity: 0.8,
                      lineHeight: 1.3,
                    }}
                  >
                    {dictamen.recomendacion}
                  </p>
                )}
              </div>
            </div>
          </Col>

          {/* Monto + plazo (BDSJEA-490: sin Garantía) */}
          <Col md={7}>
            <Row>
              <Col xs={6} className="text-center">
                <small
                  className="text-muted d-block"
                  style={{ fontSize: "0.7rem" }}
                >
                  Monto máximo
                </small>
                <strong style={{ fontSize: "1.1rem", color: theme.text }}>
                  {fmtUsd(dictamen.montoMaximoSugerido)}
                </strong>
              </Col>
              <Col
                xs={6}
                className="text-center"
                style={{ borderLeft: "1px solid #dee2e6" }}
              >
                <small
                  className="text-muted d-block"
                  style={{ fontSize: "0.7rem" }}
                >
                  Plazo sugerido
                </small>
                <strong style={{ fontSize: "1.1rem", color: "#495057" }}>
                  {dictamen.plazoRecomendadoMeses || "—"}{" "}
                  <small style={{ fontWeight: 400 }}>meses</small>
                </strong>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Observaciones como chips sutiles */}
        {dictamen.observaciones?.length > 0 && (
          <div
            className="mt-2 pt-2 d-flex flex-wrap gap-1"
            style={{ borderTop: `1px solid ${theme.border}22` }}
          >
            {dictamen.observaciones.map((obs, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded"
                style={{
                  fontSize: "0.73rem",
                  backgroundColor: `${theme.border}12`,
                  color: theme.text,
                  border: `1px solid ${theme.border}30`,
                }}
              >
                {obs}
              </span>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

// --- KPIs de Resumen ---
const KpisResumen = ({ resumen, dictamen }) => {
  if (!resumen) return null

  const kpis = [
    {
      label: "Ingreso proyectado",
      value: fmtUsd(resumen.ingresoTotalUsd),
      accent: "#3b82f6",
      icon: "mdi-trending-up",
      sub: `Agrícola: ${fmtUsd(resumen.ingresoAgricolaUsd)} | Ganadero: ${fmtUsd(resumen.ingresoGanaderoUsd)}`,
    },
    {
      label: "Margen neto",
      value: fmtUsd(resumen.margenNetoUsd),
      accent: resumen.margenNetoUsd >= 0 ? "#10b981" : "#ef4444",
      icon:
        resumen.margenNetoUsd >= 0
          ? "mdi-arrow-up-bold"
          : "mdi-arrow-down-bold",
      sub: `Rentabilidad: ${fmtNum(resumen.rentabilidadPct)}%`,
    },
    {
      label: "Capacidad pago anual",
      value: fmtUsd(resumen.capacidadPagoAnualUsd),
      accent: "#8b5cf6",
      icon: "mdi-shield-check",
      sub: `70% del margen neto`,
    },
    {
      label: "Cuota máxima mensual",
      value: fmtUsd(resumen.cuotaMaximaMensualUsd),
      accent: "#f59e0b",
      icon: "mdi-calendar-clock",
      sub: `Plazo: ${dictamen?.plazoRecomendadoMeses || 12} meses`,
    },
  ]

  return (
    <Row className="mb-3 g-2">
      {kpis.map((kpi, i) => (
        <Col md={3} sm={6} key={i}>
          <Card
            className="border-0 h-100"
            style={{
              background: `linear-gradient(135deg, ${kpi.accent}08 0%, ${kpi.accent}03 100%)`,
              borderLeft: `3px solid ${kpi.accent}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <CardBody className="py-2 px-3">
              <div className="d-flex align-items-center mb-1">
                <i
                  className={`mdi ${kpi.icon} me-1`}
                  style={{ color: kpi.accent, fontSize: "0.9rem" }}
                />
                <small
                  className="text-muted"
                  style={{
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  {kpi.label}
                </small>
              </div>
              <h5
                className="mb-0"
                style={{
                  color: kpi.accent,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
              >
                {kpi.value}
              </h5>
              <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                {kpi.sub}
              </small>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  )
}

// --- Timeline de Cosechas ---
const TimelineCosechas = ({ cultivos }) => {
  if (!cultivos || cultivos.length === 0) return null

  const sorted = [...cultivos]
    .filter(c => c.fechaCosechaEstimada)
    .sort((a, b) => (a.diasParaCosecha || 0) - (b.diasParaCosecha || 0))

  if (sorted.length === 0) return null

  // Escala: 12 meses máximo visible (365 días)
  const ESCALA_DIAS = 365
  const MESES_NOMBRES = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ]
  const hoy = new Date()

  // Generar marcadores de meses
  const marcadoresMeses = []
  for (let i = 0; i < 12; i++) {
    const fecha = new Date(hoy.getFullYear(), hoy.getMonth() + i + 1, 1)
    const diasDesdeHoy = Math.round((fecha - hoy) / (1000 * 60 * 60 * 24))
    if (diasDesdeHoy <= ESCALA_DIAS) {
      marcadoresMeses.push({
        mes: MESES_NOMBRES[fecha.getMonth()],
        anio: fecha.getFullYear().toString().slice(2),
        pct: (diasDesdeHoy / ESCALA_DIAS) * 100,
      })
    }
  }

  const coloresCultivo = {
    SOJA: "#4CAF50",
    MAIZ: "#FFC107",
    TRIGO: "#FF9800",
    GIRASOL: "#FFD600",
    SORGO: "#8D6E63",
    CEBADA: "#78909C",
  }

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">Timeline de cosechas</span>
        <small className="text-muted ms-2">(próximos 12 meses)</small>
      </CardHeader>
      <CardBody>
        {/* Marcadores de meses */}
        <div
          className="position-relative"
          style={{
            marginLeft: "140px",
            marginRight: "10px",
            height: "20px",
            marginBottom: "4px",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "1px",
              backgroundColor: "#dee2e6",
            }}
          />
          {marcadoresMeses.map((m, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${m.pct}%`,
                transform: "translateX(-50%)",
                fontSize: "0.65rem",
                color: "#adb5bd",
                textAlign: "center",
              }}
            >
              <span>{m.mes}</span>
              <div
                style={{
                  width: "1px",
                  height: "6px",
                  backgroundColor: "#dee2e6",
                  margin: "0 auto",
                }}
              />
            </div>
          ))}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#0d6efd",
            }}
          >
            HOY
          </div>
        </div>

        {/* Barras de cultivos */}
        {sorted.map((cultivo, idx) => {
          const dias = cultivo.diasParaCosecha || 0
          const pct = Math.min((dias / ESCALA_DIAS) * 100, 100)
          const excede = dias > ESCALA_DIAS
          const color = coloresCultivo[cultivo.codigoCultivo] || "#6c757d"
          const meses = Math.round(dias / 30)

          return (
            <div
              key={idx}
              className="d-flex align-items-start"
              style={{ marginBottom: "6px" }}
            >
              {/* Label */}
              <div
                style={{
                  width: "130px",
                  paddingRight: "10px",
                  textAlign: "right",
                  flexShrink: 0,
                }}
              >
                <strong style={{ fontSize: "0.82rem", lineHeight: 1.2 }}>
                  {cultivo.cultivo}
                </strong>
                {cultivo.variante && (
                  <small
                    className="text-muted d-block"
                    style={{ fontSize: "0.65rem" }}
                  >
                    {cultivo.variante}
                  </small>
                )}
              </div>

              {/* Barra + info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="position-relative" style={{ height: "26px" }}>
                  {/* Barra */}
                  <div
                    className="rounded-end"
                    style={{
                      width: pct > 0 ? `${Math.max(pct, 8)}%` : "8%",
                      height: "100%",
                      backgroundColor: color,
                      opacity: 0.75,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingRight: "8px",
                      paddingLeft: "8px",
                      borderLeft: `3px solid ${color}`,
                    }}
                  >
                    <small
                      className="text-white fw-bold"
                      style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
                    >
                      {dias <= 0
                        ? "Listo"
                        : meses <= 1
                          ? `${dias} días`
                          : `${meses} meses`}
                    </small>
                  </div>
                  {/* Indicador de cosecha al final de la barra */}
                  {!excede && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${Math.max(pct, 8)}%`,
                        top: "50%",
                        transform: "translateY(-50%)",
                        marginLeft: "4px",
                        fontSize: "0.75rem",
                      }}
                    >
                      🌾
                    </div>
                  )}
                </div>
                {/* Info debajo */}
                <small className="text-muted" style={{ fontSize: "0.68rem" }}>
                  {cultivo.mesCosecha} · {fmtNum(cultivo.superficieHa, 0)} ha
                  {cultivo.ingresoBrutoUsd > 0 && (
                    <> · {fmtUsd(cultivo.ingresoBrutoUsd)}</>
                  )}
                  {cultivo.condicionNdvi &&
                    cultivo.condicionNdvi !== "SIN_DATOS" && (
                      <span className="ms-1">
                        · NDVI:{" "}
                        <span
                          style={{
                            color: cultivo.colorCondicion,
                            fontWeight: 600,
                          }}
                        >
                          {cultivo.condicionNdvi}
                        </span>
                      </span>
                    )}
                  {excede && (
                    <span className="ms-1 text-warning">(+12 meses)</span>
                  )}
                </small>
              </div>
            </div>
          )
        })}
      </CardBody>
    </Card>
  )
}

// --- Detalle por Cultivo ---
const DetalleCultivos = ({ cultivos }) => {
  if (!cultivos || cultivos.length === 0) return null

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2 d-flex justify-content-between align-items-center">
        <span className="fw-semibold">Detalle por cultivo</span>
        <small className="text-muted">
          {cultivos.length} cultivo{cultivos.length > 1 ? "s" : ""}
        </small>
      </CardHeader>
      <CardBody className="py-2">
        {cultivos.map((c, i) => {
          const tema =
            COLORES_CULTIVO[c.codigoCultivo] || COLORES_CULTIVO.default
          const margenPositivo = (c.margenBrutoUsd || 0) >= 0

          return (
            <div
              key={i}
              className="rounded mb-2 overflow-hidden"
              style={{ border: `1px solid ${tema.border}25` }}
            >
              {/* Header del cultivo */}
              <div
                className="d-flex align-items-center justify-content-between px-3 py-2"
                style={{
                  backgroundColor: tema.bg,
                  borderBottom: `1px solid ${tema.border}20`,
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 28,
                      height: 28,
                      backgroundColor: tema.light,
                      border: `1.5px solid ${tema.border}`,
                    }}
                  >
                    <i
                      className="mdi mdi-sprout"
                      style={{ fontSize: "0.8rem", color: tema.border }}
                    />
                  </div>
                  <div>
                    <strong style={{ color: tema.text, fontSize: "0.9rem" }}>
                      {c.cultivo}
                    </strong>
                    {c.variante && (
                      <small className="text-muted ms-1">({c.variante})</small>
                    )}
                  </div>
                  {c.condicionNdvi && c.condicionNdvi !== "SIN_DATOS" && (
                    <span
                      className="px-2 py-0 rounded-pill"
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        backgroundColor: c.colorCondicion + "20",
                        color: c.colorCondicion,
                        border: `1px solid ${c.colorCondicion}40`,
                      }}
                    >
                      NDVI: {c.condicionNdvi}
                    </span>
                  )}
                </div>
                <div className="text-end">
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: tema.text,
                      opacity: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Margen bruto
                  </span>
                  <div
                    style={{
                      fontWeight: 700,
                      color: margenPositivo
                        ? PALETA.verde.text
                        : PALETA.rojo.text,
                      fontSize: "1rem",
                    }}
                  >
                    {fmtUsd(c.margenBrutoUsd)}
                  </div>
                </div>
              </div>

              {/* Métricas en grid */}
              <div className="px-3 py-2" style={{ backgroundColor: "#fff" }}>
                <div className="d-flex flex-wrap" style={{ gap: "0 24px" }}>
                  <MetricaInline
                    label="Superficie"
                    value={`${fmtNum(c.superficieHa, 0)} ha`}
                  />
                  <MetricaInline
                    label="Establecimiento"
                    value={c.establecimiento}
                    small
                  />
                  <MetricaInline
                    label="Rendimiento"
                    value={`${fmtNum(c.rendimientoAjustado)} qq/ha`}
                    sub={
                      c.factorAjusteNdvi && c.factorAjusteNdvi !== 1
                        ? `(×${fmtNum(c.factorAjusteNdvi)})`
                        : null
                    }
                  />
                  <MetricaInline
                    label="Producción"
                    value={`${fmtNum(c.produccionEstimadaTn, 0)} tn`}
                  />
                  <MetricaInline
                    label="Ingreso bruto"
                    value={fmtUsd(c.ingresoBrutoUsd)}
                    accent={PALETA.verde.text}
                  />
                  <MetricaInline
                    label="Cosecha"
                    value={c.mesCosecha || "—"}
                    sub={
                      c.diasParaCosecha > 0
                        ? `(${c.diasParaCosecha} días)`
                        : null
                    }
                  />
                  <MetricaInline
                    label="Confianza"
                    value={c.nivelConfianza || "—"}
                  />
                </div>
              </div>
            </div>
          )
        })}

        {/* Totales */}
        {cultivos.length > 1 && (
          <div
            className="d-flex justify-content-between align-items-center px-3 py-2 mt-1 rounded"
            style={{
              backgroundColor: PALETA.gris.bg,
              border: `1px solid ${PALETA.gris.border}20`,
            }}
          >
            <div>
              <strong style={{ fontSize: "0.85rem", color: PALETA.gris.text }}>
                Total
              </strong>
              <small className="text-muted ms-2">
                {fmtNum(
                  cultivos.reduce((s, c) => s + (c.superficieHa || 0), 0),
                  0,
                )}{" "}
                ha
              </small>
            </div>
            <div className="d-flex gap-4">
              <div className="text-end">
                <small
                  className="text-muted d-block"
                  style={{ fontSize: "0.65rem" }}
                >
                  Ingreso total
                </small>
                <strong style={{ color: PALETA.verde.text }}>
                  {fmtUsd(
                    cultivos.reduce((s, c) => s + (c.ingresoBrutoUsd || 0), 0),
                  )}
                </strong>
              </div>
              <div className="text-end">
                <small
                  className="text-muted d-block"
                  style={{ fontSize: "0.65rem" }}
                >
                  Margen total
                </small>
                <strong
                  style={{
                    color:
                      cultivos.reduce(
                        (s, c) => s + (c.margenBrutoUsd || 0),
                        0,
                      ) >= 0
                        ? PALETA.verde.text
                        : PALETA.rojo.text,
                  }}
                >
                  {fmtUsd(
                    cultivos.reduce((s, c) => s + (c.margenBrutoUsd || 0), 0),
                  )}
                </strong>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

const MetricaInline = ({ label, value, sub, accent, small: isSmall }) => (
  <div style={{ minWidth: isSmall ? "auto" : "90px", marginBottom: "4px" }}>
    <small
      className="text-muted d-block"
      style={{ fontSize: "0.62rem", lineHeight: 1 }}
    >
      {label}
    </small>
    <span
      style={{
        fontSize: isSmall ? "0.78rem" : "0.85rem",
        fontWeight: 500,
        color: accent || "#1f2937",
      }}
    >
      {value}
    </span>
    {sub && (
      <small className="text-muted d-block" style={{ fontSize: "0.6rem" }}>
        {sub}
      </small>
    )}
  </div>
)

// --- Flujo de Caja Mensual ---
const FlujoCajaMensual = ({ flujo }) => {
  if (!flujo || flujo.length === 0) return null

  const data = flujo.map(f => ({
    mes: f.mesAnio
      ? f.mesAnio.substring(0, 3) +
        " " +
        (f.mesAnio.split(" ")[1] || "").slice(2)
      : `M${f.mesNumero}`,
    mesCompleto: f.mesAnio,
    ingreso: f.ingresoEstimadoUsd || 0,
    costo: -(f.costoEstimadoUsd || 0),
    neto: f.flujoNetoUsd || 0,
    acumulado: f.acumuladoUsd || 0,
    esCosecha: f.mesCosecha,
    descripcion: f.descripcion,
  }))

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">Flujo de caja proyectado</span>
        <small className="text-muted ms-2">(12 meses)</small>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart
            data={data}
            margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="mes"
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickFormatter={v => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)}
            />
            <RTooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
              formatter={(value, name) => {
                const labels = {
                  ingreso: "Ingreso",
                  costo: "Costo",
                  acumulado: "Acumulado",
                }
                return [fmtUsd(Math.abs(value)), labels[name] || name]
              }}
              labelFormatter={(label, payload) => {
                if (payload?.[0]) {
                  const p = payload[0].payload
                  return `${p.mesCompleto}${p.esCosecha ? " — Cosecha" : ""}`
                }
                return label
              }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: "0.75rem" }}
            />
            <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="3 3" />

            {/* Ingresos: verde suave con barras más claras cuando no es cosecha */}
            <Bar
              dataKey="ingreso"
              name="Ingreso"
              stackId="flujo"
              radius={[3, 3, 0, 0]}
            >
              {data.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={entry.esCosecha ? "#34a853" : "#34a853"}
                  opacity={entry.esCosecha ? 0.85 : 0.25}
                />
              ))}
            </Bar>

            {/* Costos: gris rosado en vez de rojo puro */}
            <Bar
              dataKey="costo"
              name="Costo"
              stackId="flujo"
              fill="#94a3b8"
              opacity={0.4}
              radius={[0, 0, 3, 3]}
            />

            {/* Acumulado: línea azul con area fill sutil */}
            <Area
              type="monotone"
              dataKey="acumulado"
              name="Acumulado"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.06}
              strokeWidth={2}
              dot={{ r: 3, fill: "#3b82f6", stroke: "#fff", strokeWidth: 1.5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Meses de cosecha como timeline compacto */}
        {flujo.some(f => f.mesCosecha) && (
          <div className="mt-2 d-flex flex-wrap gap-1">
            {flujo
              .filter(f => f.mesCosecha)
              .map((f, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: PALETA.verde.bg,
                    color: PALETA.verde.text,
                    border: `1px solid ${PALETA.verde.border}25`,
                  }}
                >
                  <i
                    className="mdi mdi-sprout me-1"
                    style={{ fontSize: "0.7rem" }}
                  />
                  {f.mesAnio}: {f.descripcion}
                </span>
              ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

// --- Sección Ganadera ---
const SeccionGanadera = ({ ganadera }) => {
  if (!ganadera) return null

  return (
    <Card className="mb-3 border-0 shadow-sm overflow-hidden text-muted">
      <div style={{ height: "3px", background: PALETA.marron.border }} />
      <CardBody className="py-3">
        <div className="d-flex align-items-center mb-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-2"
            style={{
              width: 32,
              height: 32,
              backgroundColor: PALETA.marron.light,
              border: `1.5px solid ${PALETA.marron.border}`,
            }}
          >
            <i
              className="mdi mdi-cow"
              style={{ fontSize: "0.85rem", color: PALETA.marron.border }}
            />
          </div>
          <h6 className="mb-0" style={{ color: PALETA.marron.text }}>
            Proyección ganadera
          </h6>
        </div>

        {/* BDSJEA-167: las tres primeras son de hacienda de carne. En un tambo
            puro daban "Total cabezas 0" y "Valuación rodeo USD 0" al lado de un
            ingreso de tambo de seis cifras, que se lee como una contradicción.
            Ahora solo se muestran si hay hacienda. */}
        <div className="d-flex flex-wrap gap-3">
          {ganadera.totalCabezas > 0 && (
            <>
              <MetricaCard
                label="Total cabezas"
                value={ganadera.totalCabezas?.toLocaleString("es-AR")}
                palette={PALETA.marron}
              />
              <MetricaCard
                label="Valuación rodeo"
                value={fmtUsd(ganadera.valuacionRodeoUsd)}
                palette={PALETA.azul}
              />
              <MetricaCard
                label="Ingreso anual hacienda"
                value={fmtUsd(ganadera.ingresoEstimadoAnualUsd)}
                palette={PALETA.verde}
              />
            </>
          )}

          {/* Tambo con datos cargados: se muestran ingreso y costo por
              separado. Mostrar solo el margen bajo la etiqueta "ingreso" es
              justamente el error que hacía dar 100% de rentabilidad. */}
          {ganadera.ingresoBrutoTamboAnualUsd != null ? (
            <>
              <MetricaCard
                label="Ingreso tambo"
                value={fmtUsd(ganadera.ingresoBrutoTamboAnualUsd)}
                palette={PALETA.verde}
              />
              <MetricaCard
                label="Costos tambo"
                value={fmtUsd(ganadera.costoTamboAnualUsd)}
                palette={PALETA.marron}
              />
              <MetricaCard
                label="Margen tambo"
                value={fmtUsd(ganadera.ingresoTamboAnualUsd)}
                palette={PALETA.teal}
              />
            </>
          ) : (
            ganadera.esTambo && (
              <MetricaCard
                label="Margen tambo (estimado)"
                value={fmtUsd(ganadera.ingresoTamboAnualUsd)}
                palette={PALETA.teal}
              />
            )
          )}
        </div>
      </CardBody>
    </Card>
  )
}

const MetricaCard = ({ label, value, palette }) => (
  <div
    className="rounded px-3 py-2"
    style={{
      backgroundColor: palette.bg,
      border: `1px solid ${palette.border}20`,
      minWidth: "140px",
      flex: 1,
    }}
  >
    <small
      style={{
        fontSize: "0.65rem",
        color: palette.text,
        opacity: 0.6,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
      }}
    >
      {label}
    </small>
    <div style={{ fontSize: "1.05rem", fontWeight: 700, color: palette.text }}>
      {value}
    </div>
  </div>
)

export default ProyeccionCrediticia
